import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError, resolveImg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { VehicleFormDialog } from "@/pages/admin/VehicleFormDialog";
import { formatPrice, formatKm } from "@/components/VehicleCard";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  LogOut, Plus, Pencil, Trash2, Car, Inbox, BellDot, CheckCircle2, Eye, ExternalLink,
} from "lucide-react";
import { toast } from "sonner";

const TYPE_LABELS = {
  achat: "Achat", vente: "Vente/Reprise", recherche: "Recherche perso",
  importation: "Importation", atelier: "RDV Atelier", devis: "Devis", contact: "Contact",
};
const STATUSES = ["nouveau", "en cours", "traité"];
const statusColor = { nouveau: "bg-[#C5A059] text-black", "en cours": "bg-blue-500/20 text-blue-300", "traité": "bg-green-500/20 text-green-300" };

function StatCard({ icon: Icon, label, value, testid }) {
  return (
    <div data-testid={testid} className="bg-[#121212] border border-white/10 rounded-sm p-6">
      <div className="flex items-center justify-between">
        <span className="text-sm text-neutral-400">{label}</span>
        <Icon className="h-5 w-5 text-[#C5A059]" />
      </div>
      <p className="font-display text-4xl font-bold text-white mt-3">{value}</p>
    </div>
  );
}

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [vehicles, setVehicles] = useState([]);
  const [requests, setRequests] = useState([]);
  const [reqFilter, setReqFilter] = useState("tous");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [viewing, setViewing] = useState(null);

  const loadAll = useCallback(() => {
    api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {});
    api.get("/vehicles", { params: { status: "tous" } }).then((r) => setVehicles(r.data)).catch(() => {});
  }, []);

  const loadRequests = useCallback(() => {
    api.get("/requests", { params: reqFilter === "tous" ? {} : { type: reqFilter } })
      .then((r) => setRequests(r.data)).catch(() => {});
  }, [reqFilter]);

  useEffect(() => { loadAll(); }, [loadAll]);
  useEffect(() => { loadRequests(); }, [loadRequests]);

  const handleLogout = () => { logout(); navigate("/admin/login"); };

  const deleteVehicle = async (id) => {
    try { await api.delete(`/vehicles/${id}`); toast.success("Véhicule supprimé"); loadAll(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const deleteRequest = async (id) => {
    try { await api.delete(`/requests/${id}`); toast.success("Demande supprimée"); loadRequests(); loadAll(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const updateStatus = async (id, status) => {
    try { await api.patch(`/requests/${id}`, { status }); loadRequests(); loadAll(); toast.success("Statut mis à jour"); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-[#050505]">
      <header className="border-b border-white/10 bg-black/60 backdrop-blur-2xl sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/logo.png" alt="AS AUTO Garage" className="h-12 w-auto object-contain" />
            <span className="text-xs uppercase tracking-[0.2em] text-neutral-500 ml-1 hidden sm:inline">Administration</span>
          </div>
          <div className="flex items-center gap-4">
            <a href="/" target="_blank" rel="noreferrer" className="hidden sm:flex items-center gap-2 text-sm text-neutral-400 hover:text-white"><ExternalLink className="h-4 w-4" /> Voir le site</a>
            <span className="text-sm text-neutral-400 hidden sm:inline">{user?.email}</span>
            <Button data-testid="logout-btn" onClick={handleLogout} variant="outline" size="sm" className="border-white/20 text-white hover:bg-white/5 rounded-sm"><LogOut className="h-4 w-4 mr-2" /> Déconnexion</Button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-5 lg:px-8 py-10">
        {stats && (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4 mb-10">
            <StatCard testid="stat-vehicles" icon={Car} label="Véhicules disponibles" value={stats.available_vehicles} />
            <StatCard testid="stat-total-vehicles" icon={Car} label="Total véhicules" value={stats.total_vehicles} />
            <StatCard testid="stat-requests" icon={Inbox} label="Total demandes" value={stats.total_requests} />
            <StatCard testid="stat-new" icon={BellDot} label="Nouvelles demandes" value={stats.new_requests} />
          </div>
        )}

        <Tabs defaultValue="requests">
          <TabsList className="bg-[#121212] border border-white/10 mb-6">
            <TabsTrigger data-testid="admin-tab-requests" value="requests" className="data-[state=active]:bg-[#C5A059] data-[state=active]:text-black">Demandes clients</TabsTrigger>
            <TabsTrigger data-testid="admin-tab-vehicles" value="vehicles" className="data-[state=active]:bg-[#C5A059] data-[state=active]:text-black">Véhicules</TabsTrigger>
          </TabsList>

          {/* REQUESTS */}
          <TabsContent value="requests">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-display text-2xl font-bold text-white">Demandes reçues</h2>
              <Select value={reqFilter} onValueChange={setReqFilter}>
                <SelectTrigger data-testid="request-type-filter" className="w-[200px] bg-[#121212] border-white/15 text-white"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="tous">Tous les types</SelectItem>
                  {Object.entries(TYPE_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="bg-[#121212] border border-white/10 rounded-sm overflow-hidden">
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="border-white/10 hover:bg-transparent">
                      <TableHead className="text-neutral-400">Type</TableHead>
                      <TableHead className="text-neutral-400">Client</TableHead>
                      <TableHead className="text-neutral-400 hidden md:table-cell">Contact</TableHead>
                      <TableHead className="text-neutral-400">Statut</TableHead>
                      <TableHead className="text-neutral-400 text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {requests.length === 0 ? (
                      <TableRow><TableCell colSpan={5} className="text-center text-neutral-500 py-12">Aucune demande pour le moment.</TableCell></TableRow>
                    ) : requests.map((r) => (
                      <TableRow key={r.id} data-testid={`request-row-${r.id}`} className="border-white/10">
                        <TableCell><Badge variant="outline" className="border-[#C5A059]/40 text-[#C5A059]">{TYPE_LABELS[r.type] || r.type}</Badge></TableCell>
                        <TableCell className="text-white font-medium">{r.name}</TableCell>
                        <TableCell className="text-neutral-400 hidden md:table-cell text-sm">{r.email}<br/>{r.phone}</TableCell>
                        <TableCell>
                          <Select value={r.status} onValueChange={(v) => updateStatus(r.id, v)}>
                            <SelectTrigger data-testid={`request-status-${r.id}`} className="w-[130px] h-8 bg-transparent border-white/15 text-xs">
                              <Badge className={`${statusColor[r.status] || ""} border-0`}>{r.status}</Badge>
                            </SelectTrigger>
                            <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                          </Select>
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-1">
                            <Button data-testid={`view-request-${r.id}`} onClick={() => setViewing(r)} variant="ghost" size="icon" className="h-8 w-8 text-neutral-400 hover:text-white"><Eye className="h-4 w-4" /></Button>
                            <AlertDialog>
                              <AlertDialogTrigger asChild><Button data-testid={`delete-request-${r.id}`} variant="ghost" size="icon" className="h-8 w-8 text-neutral-400 hover:text-red-400"><Trash2 className="h-4 w-4" /></Button></AlertDialogTrigger>
                              <AlertDialogContent className="bg-[#0f0f0f] border-white/10 text-white">
                                <AlertDialogHeader><AlertDialogTitle>Supprimer cette demande ?</AlertDialogTitle><AlertDialogDescription className="text-neutral-400">Cette action est irréversible.</AlertDialogDescription></AlertDialogHeader>
                                <AlertDialogFooter>
                                  <AlertDialogCancel className="border-white/20 text-white hover:bg-white/5">Annuler</AlertDialogCancel>
                                  <AlertDialogAction onClick={() => deleteRequest(r.id)} className="bg-red-600 hover:bg-red-700">Supprimer</AlertDialogAction>
                                </AlertDialogFooter>
                              </AlertDialogContent>
                            </AlertDialog>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>
          </TabsContent>

          {/* VEHICLES */}
          <TabsContent value="vehicles">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-display text-2xl font-bold text-white">Gestion des véhicules</h2>
              <Button data-testid="add-vehicle-btn" onClick={() => { setEditing(null); setDialogOpen(true); }} className="bg-[#C5A059] text-black hover:bg-[#E6C678] rounded-sm"><Plus className="h-4 w-4 mr-2" /> Ajouter</Button>
            </div>
            <div className="bg-[#121212] border border-white/10 rounded-sm overflow-hidden">
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="border-white/10 hover:bg-transparent">
                      <TableHead className="text-neutral-400">Véhicule</TableHead>
                      <TableHead className="text-neutral-400 hidden md:table-cell">Année</TableHead>
                      <TableHead className="text-neutral-400 hidden md:table-cell">Km</TableHead>
                      <TableHead className="text-neutral-400">Prix</TableHead>
                      <TableHead className="text-neutral-400">Statut</TableHead>
                      <TableHead className="text-neutral-400 text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {vehicles.length === 0 ? (
                      <TableRow><TableCell colSpan={6} className="text-center text-neutral-500 py-12">Aucun véhicule.</TableCell></TableRow>
                    ) : vehicles.map((v) => (
                      <TableRow key={v.id} data-testid={`vehicle-row-${v.id}`} className="border-white/10">
                        <TableCell>
                          <div className="flex items-center gap-3">
                            {v.images?.[0] ? (
                              <img src={resolveImg(v.images[0])} alt="" className="h-10 w-16 object-cover rounded-sm bg-black" />
                            ) : (
                              <div className="h-10 w-16 flex items-center justify-center rounded-sm bg-white/5 border border-white/10"><Car className="h-4 w-4 text-neutral-600" /></div>
                            )}
                            <div><p className="text-white font-medium">{v.brand} {v.model}</p><p className="text-xs text-neutral-500">{v.fuel} • {v.transmission}</p></div>
                          </div>
                        </TableCell>
                        <TableCell className="text-neutral-300 hidden md:table-cell">{v.year}</TableCell>
                        <TableCell className="text-neutral-300 hidden md:table-cell">{formatKm(v.mileage)}</TableCell>
                        <TableCell className="text-[#C5A059] font-semibold">{formatPrice(v.price)}</TableCell>
                        <TableCell>
                          {v.status === "disponible"
                            ? <Badge className="bg-green-500/20 text-green-300 border-0">Disponible</Badge>
                            : <Badge className="bg-red-500/20 text-red-300 border-0">Vendu</Badge>}
                          {v.featured && <Badge className="ml-1 bg-[#C5A059]/20 text-[#C5A059] border-0">Vedette</Badge>}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-1">
                            <Button data-testid={`edit-vehicle-${v.id}`} onClick={() => { setEditing(v); setDialogOpen(true); }} variant="ghost" size="icon" className="h-8 w-8 text-neutral-400 hover:text-white"><Pencil className="h-4 w-4" /></Button>
                            <AlertDialog>
                              <AlertDialogTrigger asChild><Button data-testid={`delete-vehicle-${v.id}`} variant="ghost" size="icon" className="h-8 w-8 text-neutral-400 hover:text-red-400"><Trash2 className="h-4 w-4" /></Button></AlertDialogTrigger>
                              <AlertDialogContent className="bg-[#0f0f0f] border-white/10 text-white">
                                <AlertDialogHeader><AlertDialogTitle>Supprimer ce véhicule ?</AlertDialogTitle><AlertDialogDescription className="text-neutral-400">{v.brand} {v.model} — action irréversible.</AlertDialogDescription></AlertDialogHeader>
                                <AlertDialogFooter>
                                  <AlertDialogCancel className="border-white/20 text-white hover:bg-white/5">Annuler</AlertDialogCancel>
                                  <AlertDialogAction onClick={() => deleteVehicle(v.id)} className="bg-red-600 hover:bg-red-700">Supprimer</AlertDialogAction>
                                </AlertDialogFooter>
                              </AlertDialogContent>
                            </AlertDialog>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </div>

      <VehicleFormDialog open={dialogOpen} onOpenChange={setDialogOpen} vehicle={editing} onSaved={loadAll} />

      <Dialog open={!!viewing} onOpenChange={(o) => !o && setViewing(null)}>
        <DialogContent className="bg-[#0f0f0f] border-white/10 text-white max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display text-xl">{viewing && (TYPE_LABELS[viewing.type] || viewing.type)} — {viewing?.name}</DialogTitle></DialogHeader>
          {viewing && (
            <div className="space-y-2 text-sm">
              <Row label="Nom" value={viewing.name} />
              <Row label="E-mail" value={viewing.email} />
              <Row label="Téléphone" value={viewing.phone || "—"} />
              {Object.entries(viewing.details || {}).map(([k, val]) => <Row key={k} label={k} value={Array.isArray(val) ? val.join(", ") : String(val)} />)}
              {viewing.message && <Row label="Message" value={viewing.message} />}
              <Row label="Reçu le" value={new Date(viewing.created_at).toLocaleString("fr-FR")} />
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex gap-3 py-2 border-b border-white/5">
      <span className="text-neutral-500 min-w-[130px]">{label}</span>
      <span className="text-white flex-1">{value}</span>
    </div>
  );
}
